// RequestReceiptPrint — A4 printable receipt for a 発注依頼 (iter_143h).
//
// Opens in a dedicated tab at /shizai/requests/:id/print. Renders on-screen
// with the same layout as when printed. Auto-triggers window.print() on
// mount if `?auto=1` is passed (useful for one-tap print buttons).
//
// Layout: header + line-item table + notes + total + 3 stamp boxes
// (依頼者 · 承認者 · 資材担当承認). The 資材担当承認 box auto-fills the
// digital approver's name + timestamp when a shizai approval exists for a
// PO derived from this request; if not, the box is left blank for a
// physical hanko. The 依頼者 box auto-fills the requester's name so the
// stamp acts as a co-sign next to the printed identity.
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Render a CODE128 barcode of the given value on the passed SVG ref.
// Kept as a hook so the barcode redraws when the value changes (e.g. when
// the print view is refetched). Silent-fails if the value is empty so we
// never break the receipt when a legacy request has no request_no.
function useBarcodeSvg(value, opts) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !value) return;
    try {
      JsBarcode(el, String(value), {
        format: 'CODE128',
        width: 1.6,
        height: 34,
        margin: 0,
        displayValue: false,
        ...(opts || {}),
      });
    } catch {
      /* ignore malformed values */
    }
  }, [value, opts]);
  return ref;
}

const PRIORITY_LABELS = { low: '低', normal: '通常', high: '高', urgent: '緊急' };

function fmtYen(v) {
  const n = Number(v || 0);
  return `¥${n.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return formatDateTime(d);
  } catch { return iso; }
}

export default function RequestReceiptPrint() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [req, setReq] = useState(null);
  const [approvals, setApprovals] = useState([]); // any shizai_approvals for POs derived from this REQ
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Try the buyer endpoint first (returns full decorated data for any authed user)
        const r = await axios.get(`${API}/shizai/requests/${id}`, { withCredentials: true });
        if (cancelled) return;
        setReq(r.data);
        // Look up approvals for any child POs — we key by order_no matching REQ
        // for now (best-effort; missing approvals just render blank stamp).
        try {
          const ar = await axios.get(`${API}/shizai/approvals?request_id=${id}`, { withCredentials: true });
          if (!cancelled) setApprovals(Array.isArray(ar.data) ? ar.data : []);
        } catch {
          // Endpoint may not filter by request_id — silently ignore, the stamp
          // will fall back to the empty state.
        }
      } catch (err) {
        // Fall back to /my-requests/{id} (requester access), then the PIC
        // inbox endpoint (通知先 assignees) — both work while shizai is paused.
        try {
          const r = await axios.get(`${API}/shizai/my-requests/${id}`, { withCredentials: true });
          if (!cancelled) setReq(r.data);
        } catch (_err2) {
          try {
            const r = await axios.get(`${API}/shizai/pic-requests/${id}`, { withCredentials: true });
            if (!cancelled) setReq(r.data);
          } catch (err3) {
            if (!cancelled) setError(err3?.response?.data?.detail || err?.response?.data?.detail || '依頼を読み込めませんでした');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Auto-print if ?auto=1 (also silently skip on error)
  useEffect(() => {
    if (loading || error || !req) return;
    if (params.get('auto') === '1') {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [loading, error, req, params]);

  if (loading) {
    return <div className="p-12 text-center text-sm text-zinc-500">読み込み中…</div>;
  }
  if (error) {
    return <div className="p-12 text-center text-sm text-rose-600">{error}</div>;
  }
  return (
    <>
      {/* @media print rules — hides browser chrome, ensures A4 fit */}
      <style>{`
        @page { size: A4; margin: 14mm 14mm 14mm 14mm; }
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .receipt-page { box-shadow: none !important; padding: 0 !important; margin: 0 !important; }
          /* Also hide any floating chrome from the shell (chat FAB, toasts, etc.)
             so the print output is just the receipt. */
          [data-testid="chat-fab"], [data-sonner-toaster], .fixed { display: none !important; }
        }
        @media screen {
          body { background: #f4f4f5; }
        }
      `}</style>

      {/* On-screen toolbar (hidden in print) */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-zinc-200 px-4 py-2 flex items-center justify-between shadow-sm" data-testid="req-print-toolbar">
        <div className="text-sm text-zinc-600">
          発注依頼書プレビュー — <span className="font-mono text-zinc-800">{req.request_no}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="px-3 py-1.5 rounded bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
            data-testid="req-print-print-btn"
          >
            印刷 / PDF保存
          </button>
          <button
            type="button"
            onClick={() => window.close()}
            className="px-3 py-1.5 rounded border border-zinc-200 text-sm text-zinc-600 hover:bg-zinc-50"
            data-testid="req-print-close-btn"
          >
            閉じる
          </button>
        </div>
      </div>

      <ReceiptSheet req={req} approvals={approvals} />
    </>
  );
}

// Reusable A4 sheet — also consumed by the PIC batch print page
// (/shizai/requests/print-batch), which stacks one sheet per request.
export function ReceiptSheet({ req, approvals = [] }) {
  const lines = req?.lines || [];
  const total = lines.reduce((s, ln) => s + (Number(ln.unit_price || 0) * Number(ln.qty || 0)), 0);
  const shizaiApproval = approvals.find((a) => a.status === 'approved') || null;
  const barcodeRef = useBarcodeSvg(req?.request_no);

  return (
    <div className="receipt-page mx-auto my-6 bg-white shadow-md" style={{ width: '210mm', minHeight: '297mm', padding: '10mm 14mm', boxSizing: 'border-box', fontFamily: '"Yu Gothic", "游ゴシック", sans-serif', color: '#1a1a1a' }} data-testid="req-print-receipt">
        {/* Title */}
        <div className="flex items-end justify-between border-b-2 border-zinc-800 pb-2 mb-4">
          <div>
            <div className="text-[10px] tracking-widest text-zinc-500 uppercase">Sensbey Web App</div>
            <div className="text-2xl font-bold tracking-wider">発 注 依 頼 書</div>
          </div>
          <div className="text-right text-xs text-zinc-500 leading-snug" data-testid="req-print-header-right">
            発行日: {fmtDate(new Date().toISOString())}<br />
            <span className="font-mono text-zinc-800 text-sm">{req.request_no}</span>
            {req.request_no && (
              <div className="mt-1 flex justify-end" data-testid="req-print-barcode-wrap">
                <svg ref={barcodeRef} data-testid="req-print-barcode" style={{ height: '10mm', width: '58mm' }} />
              </div>
            )}
          </div>
        </div>

        {/* Header table */}
        <table className="w-full text-xs mb-4" style={{ borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <th className="text-left px-2 py-1 border border-zinc-300 bg-zinc-50 w-24">依頼者</th>
              <td className="px-2 py-1 border border-zinc-300">{req.requested_by || req.requested_name || req.requested_email || '—'}</td>
              <th className="text-left px-2 py-1 border border-zinc-300 bg-zinc-50 w-24">部署</th>
              <td className="px-2 py-1 border border-zinc-300">{req.department_name || '—'}</td>
            </tr>
            <tr>
              <th className="text-left px-2 py-1 border border-zinc-300 bg-zinc-50">依頼日</th>
              <td className="px-2 py-1 border border-zinc-300">{fmtDate(req.requested_at || req.created_at)}</td>
              <th className="text-left px-2 py-1 border border-zinc-300 bg-zinc-50">希望納期</th>
              <td className="px-2 py-1 border border-zinc-300">{fmtDate(req.needed_by || req.need_by) || '—'}</td>
            </tr>
            <tr>
              <th className="text-left px-2 py-1 border border-zinc-300 bg-zinc-50">分類</th>
              <td className="px-2 py-1 border border-zinc-300">{req.order_category_name || '—'}</td>
              <th className="text-left px-2 py-1 border border-zinc-300 bg-zinc-50">優先度</th>
              <td className="px-2 py-1 border border-zinc-300">{PRIORITY_LABELS[req.priority] || req.priority || '通常'}</td>
            </tr>
            <tr data-testid="req-print-delivery-dest">
              <th className="text-left px-2 py-1 border border-zinc-300 bg-zinc-50">納入先</th>
              <td className="px-2 py-1 border border-zinc-300">
                {req.delivery_destination?.name
                  ? `${req.delivery_destination.name}${req.delivery_destination.formal_name && req.delivery_destination.formal_name !== req.delivery_destination.name ? `（${req.delivery_destination.formal_name}）` : ''}`
                  : '—'}
              </td>
              <th className="text-left px-2 py-1 border border-zinc-300 bg-zinc-50">納入先コード</th>
              <td className="px-2 py-1 border border-zinc-300 font-mono">{req.delivery_destination?.code || '—'}</td>
            </tr>
          </tbody>
        </table>

        {/* Line items */}
        <table className="w-full text-xs mb-3" style={{ borderCollapse: 'collapse' }} data-testid="req-print-lines">
          <thead>
            <tr className="bg-zinc-100">
              <th className="text-center px-1 py-1 border border-zinc-300 w-8">#</th>
              <th className="text-left  px-2 py-1 border border-zinc-300">品名</th>
              <th className="text-left  px-2 py-1 border border-zinc-300 w-24">品番</th>
              <th className="text-right px-2 py-1 border border-zinc-300 w-12">数量</th>
              <th className="text-center px-1 py-1 border border-zinc-300 w-10">単位</th>
              <th className="text-right px-2 py-1 border border-zinc-300 w-20">単価</th>
              <th className="text-right px-2 py-1 border border-zinc-300 w-20">小計</th>
              <th className="text-left  px-2 py-1 border border-zinc-300 w-28">備考</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={8} className="px-2 py-4 border border-zinc-300 text-center text-zinc-400">明細なし</td></tr>
            )}
            {lines.map((ln, i) => {
              const sub = Number(ln.qty || 0) * Number(ln.unit_price || 0);
              return (
                <tr key={ln.line_id || i}>
                  <td className="text-center px-1 py-1 border border-zinc-300">{i + 1}</td>
                  <td className="px-2 py-1 border border-zinc-300">{ln.name || '—'}</td>
                  <td className="px-2 py-1 border border-zinc-300 font-mono text-[10px]">{ln.part_no || ln.sku || ''}</td>
                  <td className="text-right px-2 py-1 border border-zinc-300">{ln.qty ?? ''}</td>
                  <td className="text-center px-1 py-1 border border-zinc-300">{ln.uom || '個'}</td>
                  <td className="text-right px-2 py-1 border border-zinc-300 font-mono">{ln.unit_price ? fmtYen(ln.unit_price) : ''}</td>
                  <td className="text-right px-2 py-1 border border-zinc-300 font-mono">{sub ? fmtYen(sub) : ''}</td>
                  <td className="px-2 py-1 border border-zinc-300 text-[10px]">{ln.line_notes || ''}</td>
                </tr>
              );
            })}
            {/* Fill blanks so the table always looks like a stationery form */}
            {Array.from({ length: Math.max(0, 6 - lines.length) }).map((_, i) => (
              <tr key={`blank-${i}`}>
                <td className="text-center px-1 py-3 border border-zinc-300 text-zinc-300">·</td>
                <td className="px-2 py-3 border border-zinc-300"></td>
                <td className="px-2 py-3 border border-zinc-300"></td>
                <td className="px-2 py-3 border border-zinc-300"></td>
                <td className="px-2 py-3 border border-zinc-300"></td>
                <td className="px-2 py-3 border border-zinc-300"></td>
                <td className="px-2 py-3 border border-zinc-300"></td>
                <td className="px-2 py-3 border border-zinc-300"></td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Notes */}
        <div className="mb-3 border border-zinc-300" data-testid="req-print-notes">
          <div className="bg-zinc-50 px-2 py-1 text-[10px] font-semibold text-zinc-700 border-b border-zinc-300">依頼備考</div>
          <div className="px-2 py-2 text-xs whitespace-pre-wrap min-h-[52px]">{req.notes || ' '}</div>
        </div>

        {/* Total + attachments */}
        <div className="flex justify-between items-end mb-4">
          <div className="text-[10px] text-zinc-500">
            {(req.attachments || req.photos || []).length > 0 && (
              <>
                添付: {(req.attachments || req.photos || []).map((a) => a.filename || a.name).join(', ')}
              </>
            )}
          </div>
          <div className="text-sm">
            <span className="text-zinc-500 mr-2">合計金額（税抜）:</span>
            <span className="font-bold font-mono">{fmtYen(total)}</span>
          </div>
        </div>

        {/* Stamp boxes */}
        <div className="grid grid-cols-3 gap-3" data-testid="req-print-stamps">
          <StampBox
            title="依頼者"
            filledName={req.requested_by || req.requested_name || req.requested_email}
            filledMeta={req.requested_at ? fmtDateTime(req.requested_at) : (req.created_at ? fmtDateTime(req.created_at) : '')}
            testid="req-print-stamp-requester"
          />
          <StampBox
            title="承認者"
            filledName={''}
            filledMeta={''}
            testid="req-print-stamp-approver"
          />
          <StampBox
            title="資材担当承認"
            filledName={shizaiApproval ? (shizaiApproval.approver_name || shizaiApproval.approver_email) : ''}
            filledMeta={shizaiApproval ? fmtDateTime(shizaiApproval.decided_at || shizaiApproval.decided_ts) : ''}
            testid="req-print-stamp-shizai"
          />
        </div>

        <div className="mt-6 pt-2 text-[9px] text-zinc-400 border-t border-zinc-200 flex justify-between">
          <span>* 印は押印またはサインでお願いいたします</span>
          <span>Sensbey Web App（統合業務プラットフォーム）· 発注依頼書 · {req.request_no}</span>
        </div>
      </div>
  );
}

// One stamp cell — 依頼者/承認者/資材担当. Renders a round hanko frame with the
// digital identity written above (if known). The round frame is left empty for
// the physical stamp so the receipt stays valid as a hardcopy form.
function StampBox({ title, filledName, filledMeta, testid }) {
  return (
    <div className="border border-zinc-400 rounded-sm overflow-hidden" data-testid={testid}>
      <div className="bg-zinc-100 border-b border-zinc-300 px-2 py-1 text-[10px] font-semibold text-zinc-700 text-center">
        {title}
      </div>
      <div className="relative flex flex-col items-center justify-start" style={{ height: '92px' }}>
        {filledName && (
          <div className="w-full text-center pt-1.5 px-1">
            <div className="text-[10px] font-semibold text-zinc-800 truncate">{filledName}</div>
            {filledMeta && (
              <div className="text-[8px] text-zinc-500 leading-tight">{filledMeta}</div>
            )}
          </div>
        )}
        <div
          className="mt-auto mb-2 rounded-full border-2 border-rose-400 border-dashed opacity-70"
          style={{ width: '46px', height: '46px' }}
          title="印"
        />
      </div>
    </div>
  );
}
